import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  getKanbanInfo,
  getProductionOrdersByStatus,
} from '../../../services/productionOrderServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { getOrderStatus, BREAK_STATUS } from '../../../const/productionStatus'
import { formatDateTime, formatNumber } from '../../../utils/format'
import BreakCard from './BreakCard'

const REFRESH_INTERVAL = 30000

// O quadro só faz sentido para o que está rodando. `EM_ANDAMENTO` é o valor do
// enum da API — o antigo `5` ("Em processo") era do legado e devolve 400.
const STATUS_EM_ANDAMENTO = 'EM_ANDAMENTO'

// Cor de etapa não existe no schema legado (a API devolve `null`), então a cor
// da coluna é derivada da posição para manter a leitura do quadro.
const STAGE_COLORS = [
  '#1976d2',
  '#7b1fa2',
  '#ed6c02',
  '#2e7d32',
  '#0288d1',
  '#c2185b',
  '#5d4037',
  '#455a64',
]

const ProductionKanban = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [orders, setOrders] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [kanban, setKanban] = useState(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')

  const fetchOrders = useCallback(async () => {
    if (!subCompanyId) return
    try {
      const response = await getProductionOrdersByStatus({
        subCompanyId,
        limit: 200,
        offset: 0,
        status: STATUS_EM_ANDAMENTO,
      })
      setOrders(response.data.result || [])
    } catch (error) {
      setOrders([])
      notify(mensagemDeErro(error, 'Não foi possível carregar as ordens em andamento.'), 'error')
    }
  }, [subCompanyId, notify])

  const fetchKanban = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedOrder) return
      if (!silent) setLoading(true)
      try {
        const response = await getKanbanInfo(selectedOrder.id)
        setKanban(response.data || null)
        setUpdatedAt(new Date())
      } catch (error) {
        setKanban(null)
        notify(mensagemDeErro(error, 'Não foi possível carregar o Kanban da ordem.'), 'error')
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [selectedOrder, notify]
  )

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  useEffect(() => {
    setSelectedOrder(null)
    setKanban(null)
    setUpdatedAt(null)
  }, [subCompanyId])

  useEffect(() => {
    fetchKanban()
  }, [fetchKanban])

  // O intervalo lê a última versão da busca por ref para não se reagendar a cada render.
  const fetchKanbanRef = useRef(fetchKanban)
  useEffect(() => {
    fetchKanbanRef.current = fetchKanban
  }, [fetchKanban])

  useEffect(() => {
    if (!autoRefresh || !selectedOrder) return undefined
    const timer = setInterval(() => fetchKanbanRef.current({ silent: true }), REFRESH_INTERVAL)
    return () => clearInterval(timer)
  }, [autoRefresh, selectedOrder])

  // Filtro por status da quebra: filtra os cartões dentro de cada etapa (e a
  // contagem do cabeçalho acompanha), para achar as OPs de um status específico
  // sem se perder quando há muitas.
  const columns = useMemo(
    () =>
      ((kanban && kanban.columns) || []).map((column, index) => ({
        ...column,
        breaks: statusFilter
          ? (column.breaks || []).filter(quebra => quebra.status === statusFilter)
          : column.breaks || [],
        color: STAGE_COLORS[index % STAGE_COLORS.length],
      })),
    [kanban, statusFilter]
  )

  const order = kanban && kanban.order
  const orderPercent = order && order.progress ? Math.round(order.progress.percentComplete) : 0
  const orderStatus = order ? getOrderStatus(order.status) : null

  return (
    <Box>
      <PageHeader
        title="Kanban de Produção"
        subtitle="Acompanhamento das ordens em andamento"
        actions={
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={event => setAutoRefresh(event.target.checked)}
                />
              }
              label="Atualização automática"
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={!selectedOrder}
              onClick={() => fetchKanban()}
            >
              Atualizar agora
            </Button>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={orders}
              value={selectedOrder}
              disabled={!subCompanyId}
              getOptionLabel={option => option.numberOtherSystem || option.numberBihands || ''}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              onChange={(event, newValue) => setSelectedOrder(newValue)}
              noOptionsText="Nenhuma ordem em andamento"
              renderInput={params => <TextField {...params} label="Ordem" />}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              label="Status da quebra"
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
              disabled={!order}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(BREAK_STATUS).map(([value, { label }]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              {updatedAt
                ? `Atualizado às ${updatedAt.toLocaleTimeString('pt-BR')}`
                : 'Selecione uma ordem para acompanhar'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {order && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Ordem
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {order.numberOtherSystem || order.numberBihands || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Produto
                </Typography>
                <Typography variant="body1">{order.productName || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Criada em
                </Typography>
                <Typography variant="body2">{formatDateTime(order.createdAt)}</Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status
                </Typography>
                <Chip
                  size="small"
                  label={order.statusLabel || orderStatus.label}
                  color={orderStatus.color}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Total / Concluídas
                </Typography>
                <Typography variant="body2">
                  {formatNumber(order.quantityTotal)} / {formatNumber(order.quantityFinished)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <LinearProgress
                  variant="determinate"
                  value={orderPercent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {orderPercent}% concluído
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {kanban && (
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
          {columns.map(column => (
            <Paper
              key={column.stageName}
              variant="outlined"
              sx={{ width: 312, flexShrink: 0, borderTop: `4px solid ${column.color}` }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {column.stageName}
                  </Typography>
                  <Chip size="small" label={(column.breaks || []).length} />
                </Box>
              </Box>
              <Box
                sx={{
                  maxHeight: '65vh',
                  overflowY: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {(column.breaks || []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {statusFilter
                      ? `Nenhuma quebra "${BREAK_STATUS[statusFilter]?.label || statusFilter}" nesta etapa.`
                      : 'Nenhuma quebra nesta etapa.'}
                  </Typography>
                )}
                {(column.breaks || []).map(item => (
                  <BreakCard key={item.id} info={item} color={column.color} />
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <LoadingBackdrop open={loading} message="Carregando Kanban..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProductionKanban
